<template>
  <div role="form">
    <template v-if="loadingExisting">
      <spinner></spinner>
    </template>
    <div v-show="!loadingExisting">
      <div v-show="!sessionTypeSet">
        <div class="row m-t-m">
          <div class="col-xs-12">
            <h1 class="m-t-xs m-b-none font-heavy">
              What are you setting up registration for?
            </h1>
            <p class="text-gray-600 m-t-xs m-b-none">
              Pick the closest fit. Billing, forms, and pricing come next.
            </p>
            <div
              class="grid grid-cols-3 lg-grid-cols-2 sm-grid-cols-1 gap-m m-t-md"
            >
              <div
                v-for="card in sessionTypeCards"
                :key="card.value"
                class="session-type-card flex flex-col cursor-pointer p-md"
                :data-cy="`session-type-${card.value}`"
                @click="selectCard(card)"
              >
                <div class="flex align-items-start justify-space-between gap-x-sm">
                  <h2 class="font-heavy text-18 m-t-none m-b-none">{{ card.name }}</h2>
                  <i class="fa-solid fa-chevron-right session-type-chevron m-t-xs"></i>
                </div>
                <p class="text-gray-600 m-t-xs m-b-none">{{ card.blurb }}</p>
                <div class="flex flex-wrap gap-xs m-t-sm">
                  <span v-if="card.accountLabel" :class="card.accountBadge">{{ card.accountLabel }}</span>
                  <span v-for="chip in card.chips" :key="chip" class="badge-gray">
                    {{ chip }}
                  </span>
                </div>
                <div
                  v-if="card.teaser"
                  class="bg-green-50 rounded-xxs p-sm m-t-sm"
                >
                  <span class="badge-green">New</span>
                  <p class="text-14 text-green-800 m-t-xs m-b-xs">{{ card.teaser }}</p>
                  <a
                    :href="card.teaserLink"
                    class="flex align-items-center gap-x-xxs text-14 text-green-700 font-semi text-underline"
                    @click.stop
                  ><i class="fa-solid fa-bag-shopping"></i>See WIQ Gear packages</a>
                </div>
                <div class="m-t-sm">
                  <div class="border-t border-gray-200 p-y-xs">
                    <a
                      href="javascript:void(0)"
                      class="session-type-toggle"
                      @click.stop="card.showGets = !card.showGets"
                    >
                      <span class="session-type-toggle-caret">
                        <i :class="card.showGets ? 'fa-solid fa-caret-down' : 'fa-solid fa-caret-right'"></i>
                      </span>
                      What registrants get
                    </a>
                    <ul v-show="card.showGets" class="list-none p-l-m m-t-xs m-b-none text-14">
                      <li
                        v-for="item in card.gets"
                        :key="item.text"
                        class="flex align-items-start gap-x-xs m-b-xxs"
                      >
                        <span class="session-type-list-icon">
                          <i :class="registrantIconClass(item.icon)"></i>
                        </span>
                        <span>{{ item.text }}</span>
                      </li>
                    </ul>
                  </div>
                  <div class="border-t border-gray-200 p-y-xs">
                    <a
                      href="javascript:void(0)"
                      class="session-type-toggle"
                      @click.stop="card.showUsedFor = !card.showUsedFor"
                    >
                      <span class="session-type-toggle-caret">
                        <i :class="card.showUsedFor ? 'fa-solid fa-caret-down' : 'fa-solid fa-caret-right'"></i>
                      </span>
                      Commonly used for
                    </a>
                    <ul v-show="card.showUsedFor" class="list-none p-l-m m-t-xs m-b-none text-14 text-gray-700">
                      <li
                        v-for="use in card.usedFor"
                        :key="use"
                        class="flex align-items-start gap-x-xs m-b-xxs"
                      >
                        <span class="session-type-list-icon text-gray-500">&bull;</span>
                        <span>{{ use }}</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <div
                  class="m-t-auto border-t border-gray-200 p-t-sm flex flex-wrap align-items-center justify-space-between gap-x-sm gap-y-xs"
                >
                  <a
                    v-if="card.articleId"
                    href="javascript:void(0)"
                    class="flex align-items-center gap-x-xs text-gray-600 text-14 no-underline"
                    @click.stop="openHelpArticle(card.articleId)"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="1.5"
                      stroke="currentColor"
                      class="w-4 h-4 flex-shrink-0"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M15.91 11.672a.375.375 0 010 .656l-5.603 3.113a.375.375 0 01-.557-.328V8.887c0-.286.307-.466.557-.327l5.603 3.112z"
                      />
                    </svg>
                    {{ card.helpLabel || 'Watch example' }}
                  </a>
                  <span v-else></span>
                  <span class="text-green-700 font-semi text-14">{{ cardCta(card) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-show="sessionTypeSet">
        <div class="row m-t-m">
          <div class="col-md-4">
            <h1 class="form-section-title">Session Info</h1>
            <!-- WIQ Recommendation Section -->
            <div
              v-if="fullSeason || recurring"
              class="m-t-sm relative border border-solid border-green-600 rounded-xxs max-w-2xl m-b-only-xs-m"
            >
              <div
                class="flex align-items-center justify-center gap-sm bg-green-600 text-green-50 uppercase tracking-wide p-x-m p-y-xs rounded-t-xxs"
              >
                <span class="text-heavy">WIQ RECOMMENDATION</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="w-6 h-6 text-green-50"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18"
                  />
                </svg>
              </div>
              <div class="p-x-lg p-y-md">
                <p class="font-heavy m-b-xs">
                  Create distinct registrations based on your practice groups
                  (age/skill)
                </p>
                <p class="text-14 text-gray-600 m-t-sm m-b-xs">
                  For example, you might have two separate registrations:
                </p>
                <ul class="text-14 text-gray-600 m-t-none p-l-lg">
                  <li>
                    Beginners
                    <span v-if="fullSeason">{{ currentYear }} Folkstyle</span>
                  </li>
                  <li>
                    Advanced
                    <span v-if="fullSeason">{{ currentYear }} Folkstyle</span>
                  </li>
                </ul>
                <p>
                  WrestlingIQ automatically syncs registrants to
                  rosters—separating the registrations makes it easier for you
                  to communicate with each group independently.
                </p>
              </div>
            </div>
          </div>
          <div class="col-md-8">
            <div v-if="replicatingSession" class="alert alert-info">
              <p>
                <strong>Replicating {{ replicateSession.name }}</strong
                >. Before saving, make sure you take a close look at:
              </p>
              <ul>
                <li>Name</li>
                <li>Dates</li>
                <li>
                  <strong>Installment plans,</strong> which will likely have
                  incorrect billing dates and need to be updated.
                </li>
                <li>
                  If you are replicating a camp/one time session, and you
                  <a
                    class="text-underline"
                    href="https://help.wrestlingiq.com/article/67-customize-questions-for-a-specific-one-time-event"
                    target="_blank"
                  >
                    customized the registration questions</a
                  >, you will need to edit the questions after creating this new
                  session.
                </li>
              </ul>
            </div>
            <div v-if="replicatingSession && replicateInstallmentDatesCleared" class="alert alert-danger">
              <strong>Heads up:</strong> one or more installment plans had billing dates in the past, so we cleared those dates for you. You must open each installment plan below and set new dates before this session can be saved.
            </div>
            <div class="form-group">
              <label for="session_name">Name</label>
              <input
                id="session_name"
                v-model="name"
                type="text"
                class="form-control"
              />
            </div>
            <div class="form-group">
              <label class="m-r-sm"> Type of event </label>
              <select
                v-model="sessionType"
                class="form-control"
                :disabled="existingSessionId"
              >
                <option selected disabled>Choose session type...</option>
                <option value="full">Season</option>
                <option value="guest">Camp / Clinic</option>
                <option value="dropin">Drop In</option>
                <option value="recurring">Recurring Subscription</option>
                <option value="trial">Trial</option>
                <option value="team_registration">Travel / Dual Team</option>
                <option value="anonymous">Other</option>
              </select>
            </div>
            <div class="form-group" v-if="accountChoiceRequired" data-cy="account-type-picker">
              <label class="m-b-none">
                What kind of account should registrants get?
              </label>
              <p class="text-gray-600 m-b-xs">
                <span v-if="existingSessionId">
                  Locked after creation. Archive and replicate this session to
                  change it.
                </span>
                <span v-else>
                  Pick based on how you run this event. This cannot be changed
                  after the session is created.
                </span>
              </p>
              <div class="grid grid-cols-2 sm-grid-cols-1 md-grid-cols-1 gap-sm">
                <label
                  class="account-type-card flex flex-col p-md cursor-pointer m-b-none font-normal"
                  :class="{ 'account-type-card--active': wiqAccountType === 'full' }"
                >
                  <span class="badge-green align-self-flex-start">Full WrestlingIQ accounts</span>
                  <span class="flex align-items-start gap-x-sm m-t-sm">
                    <input
                      type="radio"
                      value="full"
                      data-cy="account-type-full"
                      v-model="wiqAccountType"
                      :disabled="!!existingSessionId && !!wiqAccountType"
                      class="m-t-xxs"
                    />
                    <ul class="list-none p-l-n m-t-none m-b-none text-14">
                      <li class="flex align-items-start gap-x-xs m-b-xxs">
                        <i class="m-t-xxs" :class="registrantIconClass('yes')"></i>
                        <span>WrestlingIQ login and mobile app</span>
                      </li>
                      <li class="flex align-items-start gap-x-xs m-b-xxs">
                        <i class="m-t-xxs" :class="registrantIconClass('yes')"></i>
                        <span>All WrestlingIQ features such as messaging, schedule, and billing</span>
                      </li>
                      <li v-if="dropin" class="flex align-items-start gap-x-xs m-b-xxs">
                        <i class="m-t-xxs" :class="registrantIconClass('yes')"></i>
                        <span>Registrants can see their remaining class passes</span>
                      </li>
                    </ul>
                  </span>
                </label>
                <label
                  class="account-type-card flex flex-col p-md cursor-pointer m-b-none font-normal"
                  :class="{ 'account-type-card--active': wiqAccountType === 'guest' }"
                >
                  <span class="badge-blue align-self-flex-start">Guest checkout</span>
                  <span class="flex align-items-start gap-x-sm m-t-sm">
                    <input
                      type="radio"
                      value="guest"
                      data-cy="account-type-guest"
                      v-model="wiqAccountType"
                      :disabled="!!existingSessionId && !!wiqAccountType"
                      class="m-t-xxs"
                    />
                    <ul class="list-none p-l-n m-t-none m-b-none text-14">
                      <li class="flex align-items-start gap-x-xs m-b-xxs">
                        <i class="m-t-xxs" :class="registrantIconClass('yes')"></i>
                        <span>Fast checkout, no password</span>
                      </li>
                      <li class="flex align-items-start gap-x-xs m-b-xxs">
                        <i class="m-t-xxs" :class="registrantIconClass('yes')"></i>
                        <span>Pre-filled forms for returning families</span>
                      </li>
                      <li class="flex align-items-start gap-x-xs m-b-xxs">
                        <i class="m-t-xxs" :class="registrantIconClass('yes')"></i>
                        <span>Parent and wrestler records are still created</span>
                      </li>
                      <li class="flex align-items-start gap-x-xs m-b-xxs">
                        <i class="m-t-xxs" :class="registrantIconClass('no')"></i>
                        <span>No WrestlingIQ login or mobile app</span>
                      </li>
                      <li class="flex align-items-start gap-x-xs m-b-xxs">
                        <i class="m-t-xxs" :class="registrantIconClass('no')"></i>
                        <span>Messages delivered by email only</span>
                      </li>
                    </ul>
                  </span>
                </label>
              </div>
            </div>
            <div class="form-group" v-show="notAnonymous">
              <label class="m-r-sm">
                Who can participate in this session?
                <VDropdown theme="info-tooltip">
                  <!-- This will be the popover target (for the events and position) -->
                  <i class="fa-solid fa-question"></i>
                  <!-- This will be the content of the popover -->
                  <template v-slot:popper>
                    <div>
                      <p class="header">What happens if I choose 'Adult'?</p>
                      <p>
                        Adult sessions streamline the registration experience
                        for sessions where registrants are all 18+.
                      </p>
                      <p>
                        For most clubs, 'kids' is the correct option, which
                        ensures registration is done by the guardian on behalf
                        of the child.
                      </p>
                    </div>
                  </template>
                </VDropdown>
              </label>
              <select v-model="participants" class="form-control">
                <option value="kids">Kids</option>
                <option value="adults">Adults</option>
              </select>
            </div>
            <div class="form-group" v-show="!recurring && !dropin && !trial">
              <label>
                <span v-text="dateLabelText"></span>
                <span v-if="anonymous">Dates</span><span v-else>dates</span>
                <VDropdown theme="info-tooltip">
                  <!-- This will be the popover target (for the events and position) -->
                  <i class="fa-solid fa-question"></i>
                  <!-- This will be the content of the popover -->
                  <template v-slot:popper>
                    <div>
                      <p class="header">
                        The date field represents the start and end date of your
                        <span v-if="anonymous">event</span
                        ><span v-else v-text="dateLabelTextLower"></span>.
                      </p>
                      <p>
                        Your online registration will be open until the end
                        date, but you can always close it earlier if needed.
                      </p>
                    </div>
                  </template>
                </VDropdown>
              </label>
              <date-pair-picker
                :initial-start="start"
                :initial-end="end"
                @dateRangeChanged="dateRangeChanged"
              ></date-pair-picker>
            </div>
            <div class="form-group" v-if="!loadingLocations && locations.length > 0">
              <label>Location</label>
              <div>
                <select
                  v-model="selectedLocationId"
                  class="form-control"
                  data-cy="location-select"
                >
                  <option :value="null">No location</option>
                  <option
                    v-for="location in selectableLocations"
                    :key="location.id"
                    :value="location.id"
                  >
                    {{ location.name }}<span v-if="location.archived"> (archived)</span>
                  </option>
                </select>
                <p class="text-gray-600 text-12 m-t-xs">
                  Used to filter sessions and registrations by location.
                  <a
                    href="/settings/team/locations"
                    class="text-underline"
                  >
                    Manage locations
                    <i class="fa-solid fa-up-right-from-square" aria-hidden="true"></i>
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
        <div class="row m-t-xl">
          <template v-if="recurring">
            <div class="col-md-4">
              <h1 class="form-section-title">Subscription Prices</h1>
              <div class="m-t-sm">
                <p class="text-14 text-gray-600 font-semi m-b-xxs">
                  <i class="fa-solid fa-circle-info m-r-xxs"></i>
                  Registrants pick one price at checkout
                </p>
                <p class="text-12 text-gray-500 m-b-none">
                  Add as many as you offer, for example monthly, a discounted
                  annual rate, or 3, 6, and 12 month contract tiers.
                </p>
              </div>
            </div>
            <div class="col-md-8">
              <div class="-m-t-xs">
                <div
                  v-for="price in subscriptionPrices"
                  :key="price.id ? `sp-${price.id}` : `new-${price.newOptionId}`"
                  class="m-t-xs"
                >
                  <subscription-price-form
                    :current-billing-partner="currentBillingPartner"
                    :price="price"
                    v-if="
                      !price.is_wiq_fee &&
                      !price.is_tax_item &&
                      !price.is_one_time_fee
                    "
                  ></subscription-price-form>
                </div>
              </div>
              <div class="card-bordered-dashed m-t-xs" v-show="showSaveButton">
                <div class="row">
                  <div class="col-xs-12">
                    <a @click.prevent="addNewSubPrice">
                      <i class="fa-solid fa-plus"></i>
                      {{ hasSubscriptionPrice ? 'Add another subscription price' : 'Add a subscription price' }}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="col-md-4">
              <h1 class="form-section-title">Payment Options</h1>
              <div v-if="!teamRegistration" class="m-t-sm">
                <p class="text-14 text-gray-600 font-semi m-b-xxs">
                  <i class="fa-solid fa-circle-info m-r-xxs"></i>
                  Registrants pick one option at checkout
                </p>
                <p class="text-12 text-gray-500 m-b-none">
                  Add as many as you offer, for example pay in full, a payment
                  plan, or an early-bird price.
                </p>
              </div>
              <div v-if="teamRegistration" class="m-t-sm">
                <p class="text-14 text-gray-600 font-semi m-b-xxs">
                  <i class="fa-solid fa-circle-info m-r-xxs"></i>
                  Application-based registration
                </p>
                <p class="text-12 text-gray-500 m-b-none">
                  Wrestlers submit their registration and payment info, but
                  their card will not be charged until you review and finalize.
                  Use the registration management screen to accept or decline
                  applications. You can optionally require a commitment deposit
                  that is collected upfront when the wrestler applies.
                </p>
              </div>
            </div>
            <div class="col-md-8">
              <div v-if="sessionType" class="-m-t-xs">
                <div
                  v-for="po in paymentOptions"
                  :key="po.id ? `po-${po.id}` : `new-${po.newOptionId}`"
                  class="m-t-xs"
                >
                  <payment-option-form
                    :payment-option="po"
                    :allow-sib-discount="notAnonymous && !teamRegistration"
                    :allow-class-pass="dropin || trial"
                    :allow-deposit="teamRegistration"
                  ></payment-option-form>
                </div>
                <div
                  class="card-bordered-dashed m-t-xs"
                  v-show="showSaveButton"
                >
                  <div class="row">
                    <div class="col-xs-12">
                      <a @click.prevent="addNewOption">
                        <i class="fa-solid fa-plus"></i>
                        {{ paymentOptions.length ? 'Add another payment option' : 'Add a payment option' }}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else>
                Pick a session type to configure your payment options
              </div>
            </div>
          </template>
        </div>
        <div class="row m-t-xl">
          <div class="col-md-4">
            <h1 class="form-section-title">Settings</h1>
          </div>
          <div class="col-md-8">
            <div class="form-group flex align-items-start">
              <input
                id="registration_open"
                type="checkbox"
                v-model="registrationOpen"
              />
              <div class="m-l-sm">
                <label for="registration_open" class="m-b-none">
                  Is registration open?
                </label>
                <p class="text-gray-600">
                  Uncheck if you do not want registration open yet.
                </p>
              </div>
            </div>
            <div
              v-if="notAnonymous && !recurring && !dropin && !teamRegistration"
            >
              <div
                class="form-group flex align-items-start"
                :class="{ 'm-b-none': hasCapacityLimit }"
              >
                <input
                  id="reg_limit"
                  type="checkbox"
                  v-model="hasCapacityLimit"
                />
                <div class="m-l-sm">
                  <label for="reg_limit" class="m-b-none">
                    Close registration after a certain # of wrestlers?
                  </label>
                  <p
                    class="text-gray-600"
                    :class="{ 'm-b-xxs': hasCapacityLimit }"
                  >
                    Not recommended unless you have a strict capacity limit at
                    your facility.
                  </p>
                </div>
              </div>
              <div
                v-if="hasCapacityLimit"
                class="m-b-md m-l-lg"
                style="max-width: 100px; margin-left: 28px"
              >
                <input
                  type="number"
                  v-model.number="capacityLimit"
                  class="form-control"
                />
              </div>
            </div>
            <div
              v-if="(campOrClinic || fullSeason) && !teamRegistration"
              :class="insuranceAlertClass"
            >
              <div
                class="alert alert-warning m-b-none"
                v-if="!billingPartnerAllowsInsurance"
              >
                Offering insurance is only available to teams using WrestlingIQ
                Payments for billing. Please email support@wrestlingiq.com if
                you want to switch over to WrestlingIQ Payments.
              </div>
              <div
                class="alert alert-success m-b-none"
                v-else-if="highlightInsuranceValue"
              >
                <strong>New in WrestlingIQ!</strong> Allow your registrants to
                purchase cancellation and gap medical insurance.
                <a
                  href="https://help.wrestlingiq.com/article/198-offering-insurance-to-registrants-cancellation-and-gap-medical-coverage"
                  class="text-underline"
                  target="_blank"
                >
                  Read more about insurance
                  <i class="fa-solid fa-up-right-from-square" aria-hidden="true"></i>
                </a>
              </div>
              <div
                class="form-group flex align-items-start"
                :class="insuranceBodyClass"
              >
                <input
                  id="show_insurance"
                  type="checkbox"
                  v-model="showInsurance"
                  :disabled="!billingPartnerAllowsInsurance"
                />
                <div class="m-l-sm">
                  <label for="show_insurance" class="m-b-none">
                    Allow customers to purchase insurance?
                  </label>
                  <p class="text-gray-600">
                    Leave checked to allow registrants to purchase cancellation
                    and gap medical insurance. This helps protect you from
                    unexpected refunds. Read more about
                    <a
                      href="https://help.wrestlingiq.com/article/198-offering-insurance-to-registrants-cancellation-and-gap-medical-coverage"
                      class="text-underline"
                      target="_blank"
                    >
                      our insurance offerings
                      <i class="fa-solid fa-up-right-from-square" aria-hidden="true"></i>
                    </a>
                  </p>
                </div>
              </div>
            </div>
            <!-- Additional Settings - Collapsed by default -->
            <div class="m-t-m" v-if="hasAdditionalSettings">
              <div
                class="cursor-pointer flex align-items-baseline gap-x-xs"
                @click="showAdditionalSettings = !showAdditionalSettings"
              >
                <i
                  :class="
                    showAdditionalSettings
                      ? 'fa-solid fa-chevron-down'
                      : 'fa-solid fa-chevron-right'
                  "
                ></i>
                <h3 class="m-y-none">Additional Settings</h3>
              </div>
              <div v-show="showAdditionalSettings" class="m-t-sm">
                <div class="form-group flex align-items-start">
                  <input
                    id="hide_from_team_reg_page"
                    type="checkbox"
                    v-model="hideFromTeamRegPage"
                  />
                  <div class="m-l-sm">
                    <label for="hide_from_team_reg_page" class="m-b-none">
                      Hide from team registration page
                    </label>
                    <p class="text-gray-600">
                      Check to keep this registration off of your public team
                      registration page. The registration link will still work
                      for anyone you send it to directly — useful when you want
                      to share with a select group before opening it up to
                      everyone.
                    </p>
                  </div>
                </div>
                <div class="form-group" v-if="notAnonymous">
                  <label class="m-b-xs">
                    Which documents must registrants sign?
                  </label>
                  <p class="text-gray-600 m-b-xs">
                    By default registrants see every team document that requires a signature. Pick specific ones if this registration should differ.
                  </p>
                  <div class="flex gap-sm m-b-xs">
                    <label class="flex align-items-center gap-xs cursor-pointer">
                      <input
                        type="radio"
                        :value="false"
                        v-model="useSpecificDocuments"
                        @change="onUseSpecificDocumentsChange"
                      />
                      <span>Use all team documents</span>
                    </label>
                    <label class="flex align-items-center gap-xs cursor-pointer">
                      <input
                        type="radio"
                        :value="true"
                        v-model="useSpecificDocuments"
                        @change="onUseSpecificDocumentsChange"
                      />
                      <span>Pick specific documents</span>
                    </label>
                  </div>
                  <div v-if="useSpecificDocuments">
                    <div v-if="availableDocuments.length === 0" class="text-gray-500">
                      <p class="m-b-none">
                        No liability or signature-required documents have been created yet.
                        <a href="/documents/new?type=liability">Create a liability form</a>
                        or
                        <a href="/documents/new?type=signature">create a signed document</a>.
                      </p>
                    </div>
                    <div v-else>
                      <p class="text-gray-600 text-14 m-b-xs">
                        Pick the liability forms and signature-required documents for this registration.
                      </p>
                      <div v-for="doc in availableDocuments" :key="doc.id" class="m-b-xs">
                        <label class="cursor-pointer m-b-none">
                          <input
                            type="checkbox"
                            :value="doc.id"
                            v-model="linkedDocumentIds"
                            @change="linkedDocsDirty = true"
                          />
                          {{ doc.name }}
                          <span v-if="doc.liability_form" class="text-12 text-gray-500">(Liability)</span>
                          <span v-else-if="doc.needs_signature" class="text-12 text-gray-500">(Signature required)</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  class="form-group"
                  v-if="createRoster && !existingSessionId"
                >
                  <label class="m-b-xs">
                    Which coaches should be added to the messaging group?
                  </label>
                  <p class="text-gray-600 m-b-xs">
                    Applies to the new roster's messaging group created in
                    Automations above.
                  </p>
                  <div class="flex gap-sm m-b-xs">
                    <label
                      class="flex align-items-center gap-xs cursor-pointer"
                    >
                      <input
                        type="radio"
                        :value="true"
                        v-model="allCoachesInMessageGroup"
                      />
                      <span>Add all coaches</span>
                    </label>
                    <label
                      class="flex align-items-center gap-xs cursor-pointer"
                    >
                      <input
                        type="radio"
                        :value="false"
                        v-model="allCoachesInMessageGroup"
                      />
                      <span>Only add specific coaches</span>
                    </label>
                  </div>
                  <div v-if="!allCoachesInMessageGroup">
                    <p class="text-gray-600 text-14 m-b-xs">
                      Search for coaches to add. You are auto-added to any
                      group you create.
                    </p>
                    <search
                      class="search-form"
                      response-key="coaches"
                      placeholder="Type a coach's name..."
                      :mutate-history="false"
                      :allow-add="false"
                      only-dismiss-model="CoachProfile"
                      :small-input="true"
                      :display-all-on-empty="false"
                      readable-model-name="coach"
                      base-url="/api/v1/coaches"
                    >
                      <template v-slot:list="slotProps">
                        <div
                          v-if="slotProps.result.id === currentProfileId"
                          class="border-top m-t-sm m-b-xs p-t-sm p-b-xs flex justify-space-between align-items-center p-r-sm text-gray-500"
                        >
                          <div>{{ slotProps.result.full_name }}</div>
                          <div class="font-small">Auto-added (you)</div>
                        </div>
                        <div
                          v-else-if="isCoachSelected(slotProps.result)"
                          class="border-top m-t-sm m-b-xs p-t-sm p-b-xs flex justify-space-between align-items-center p-r-sm text-gray-500"
                        >
                          <div>{{ slotProps.result.full_name }}</div>
                          <div class="font-small">Already added</div>
                        </div>
                        <div
                          v-else
                          class="border-top m-t-sm m-b-xs p-t-sm p-b-xs cursor-pointer flex justify-space-between align-items-center p-r-sm"
                          @click="addCoach(slotProps.result)"
                        >
                          <div>{{ slotProps.result.full_name }}</div>
                          <div
                            class="border rounded-sm border-blue-200 border-solid font-small"
                            style="padding: 2px 6px"
                          >
                            Add
                          </div>
                        </div>
                      </template>
                    </search>
                    <div
                      class="flex flex-wrap gap-x-xxs gap-y-xxs m-t-sm m-b-sm"
                    >
                      <span
                        class="btn btn-outline btn-success rounded-lg font-small"
                        style="padding: 2px 6px; cursor: default; opacity: 0.85"
                        title="You are auto-added to any group you create"
                      >
                        You
                      </span>
                      <button
                        v-for="coach in selectedCoaches"
                        :key="coach.id"
                        @click="removeCoach(coach)"
                        class="btn btn-outline btn-success rounded-lg font-small"
                        style="padding: 2px 6px"
                      >
                        {{ coach.full_name }}
                        <i class="fa-solid fa-xmark"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <div
                  class="form-group flex align-items-start"
                  v-if="teamHasUsawRequired"
                >
                  <input
                    id="bypass_usaw"
                    type="checkbox"
                    v-model="bypassUsawCollection"
                  />
                  <div class="m-l-sm">
                    <label for="bypass_usaw" class="m-b-none">
                      Bypass USAW information collection
                    </label>
                    <p class="text-gray-600">
                      Check if you do not want USAW information collected for
                      this specific registration. You can
                      <a
                        href="/settings?tab=insurance"
                        target="_blank"
                        class="text-underline"
                        >modify your global USAW configuration here.
                        <i class="fa-solid fa-up-right-from-square"></i
                      ></a>
                    </p>
                  </div>
                </div>
                <div
                  class="form-group flex align-items-start"
                  v-if="teamHasAauRequired"
                >
                  <input
                    id="bypass_aau"
                    type="checkbox"
                    v-model="bypassAauCollection"
                  />
                  <div class="m-l-sm">
                    <label for="bypass_aau" class="m-b-none">
                      Bypass AAU information collection
                    </label>
                    <p class="text-gray-600">
                      Check if you do not want AAU information collected for
                      this specific registration. You can
                      <a
                        href="/settings?tab=insurance"
                        target="_blank"
                        class="text-underline"
                        >modify your global AAU configuration here.
                        <i class="fa-solid fa-up-right-from-square"></i
                      ></a>
                    </p>
                  </div>
                </div>
                <div
                  class="form-group flex align-items-start"
                  v-if="notAnonymous && !recurring && !dropin"
                >
                  <input
                    id="multi_sign_up"
                    type="checkbox"
                    v-model="allowMultipleSignupsPerWrestler"
                  />
                  <div class="m-l-sm">
                    <label for="multi_sign_up" class="m-b-none">
                      Allow wrestlers to sign up more than once
                      <VDropdown theme="info-tooltip">
                        <!-- This will be the popover target (for the events and position) -->
                        <i class="fa-solid fa-question"></i>
                        <!-- This will be the content of the popover -->
                        <template v-slot:popper>
                          <div>
                            <p class="header">
                              Why would a wrestler need to sign up twice?
                            </p>
                            <p>
                              If you are setting up a registration for something
                              like a private lesson, you'll want to make sure
                              wrestlers can register/pay for this session more
                              than once.
                            </p>
                            <p>
                              <b>In most cases this should remain unchecked.</b>
                            </p>
                          </div>
                        </template>
                      </VDropdown>
                    </label>
                    <p class="text-gray-600">
                      In most cases, this should remain unchecked.
                    </p>
                  </div>
                </div>
                <div
                  class="form-group flex align-items-start"
                  v-if="
                    notAnonymous &&
                    !recurring &&
                    !dropin &&
                    !trial &&
                    !teamRegistration
                  "
                >
                  <input
                    id="global_sib"
                    type="checkbox"
                    v-model="excludeFromGSibDiscount"
                  />
                  <div class="m-l-sm">
                    <label for="global_sib" class="m-b-none">
                      Exclude from global sibling discounts
                      <VDropdown theme="info-tooltip">
                        <!-- This will be the popover target (for the events and position) -->
                        <i class="fa-solid fa-question"></i>
                        <!-- This will be the content of the popover -->
                        <template v-slot:popper>
                          <div>
                            <p class="header">
                              What is global sibling discount?
                            </p>
                            <p>
                              Sibling discounts are given when a kid registering
                              for this session has a sibling who has registered
                              for a different session that is happening at the
                              same time or if multiple siblings register for the
                              same paid session.
                            </p>
                            <p>
                              In some situations you may not want sessions to
                              count towards this calculation.
                              <b>In most cases this should remain unchecked.</b>
                            </p>
                          </div>
                        </template>
                      </VDropdown>
                    </label>
                    <p class="text-gray-600">
                      In most cases, this should remain unchecked.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row m-t-xl" v-if="teamRegistration">
          <div class="col-md-4">
            <h1 class="form-section-title">Divisions</h1>
          </div>
          <div class="col-md-8">
            <div v-if="loadingDivisionStandards" class="p-md">
              <spinner></spinner>
            </div>
            <div v-else>
              <div class="form-group">
                <label>Age/Weight/Gender Divisions</label>
                <select
                  v-model="selectedDivisionStandardId"
                  class="form-control"
                  :disabled="!!existingSessionId"
                  @change="onDivisionStandardChange"
                >
                  <option :value="null">Select division criteria...</option>
                  <option
                    v-for="standard in divisionStandards"
                    :key="standard.id"
                    :value="standard.id"
                  >
                    {{ standard.name }}
                  </option>
                </select>
                <p class="text-gray-600 m-t-xs">
                  This determines the groups available to wrestlers during registration.
                  <a
                    href="/settings/team/division_standards"
                    class="text-underline"
                  >
                    Create a custom division here.
                    <i class="fa-solid fa-up-right-from-square" aria-hidden="true"></i>
                  </a>
                </p>
              </div>
              <div v-if="selectedDivisionStandard" class="m-t-md">
                <label>Available Age Divisions</label>
                <p class="text-gray-600 m-b-sm">
                  Check the divisions you want available for this registration.
                  Uncheck any you don't need.
                </p>
                <div class="flex flex-col gap-sm">
                  <div
                    v-for="division in selectedDivisionStandard.age_divisions"
                    :key="division.id"
                    class="flex align-items-start"
                  >
                    <input
                      type="checkbox"
                      :id="'division_' + division.id"
                      :value="division.id"
                      v-model="enabledAgeDivisionIds"
                      :disabled="!!existingSessionId"
                      class="m-t-xxs"
                    />
                    <div class="m-l-sm">
                      <label :for="'division_' + division.id" class="m-b-none">
                        {{ division.display_name }}
                      </label>
                      <p
                        v-if="
                          division.weight_classes &&
                          division.weight_classes.length > 0
                        "
                        class="text-12 text-gray-600 m-b-none"
                      >
                        {{ formatWeightClasses(division.weight_classes) }}
                      </p>
                    </div>
                  </div>
                </div>
                <div v-if="!existingSessionId" class="m-t-sm">
                  <a
                    href="#"
                    class="text-14"
                    @click.prevent="selectAllDivisions"
                  >
                    Select all
                  </a>
                  <span class="text-gray-400 m-x-xs">|</span>
                  <a
                    href="#"
                    class="text-14"
                    @click.prevent="deselectAllDivisions"
                  >
                    Deselect all
                  </a>
                </div>
                <p v-else class="text-gray-600 m-t-sm text-12">
                  Divisions cannot be changed after creation because existing registrations depend on them.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div class="row m-t-xl">
          <div class="col-md-4">
            <h1 class="form-section-title">Automations</h1>
          </div>
          <div class="col-md-8">
            <div
              class="form-group flex align-items-start"
              v-if="!existingSessionId && notAnonymous"
            >
              <input
                id="create_roster"
                type="checkbox"
                v-model="createRoster"
              />
              <div class="m-l-sm">
                <label for="create_roster" class="m-b-none">
                  Create a roster that tracks wrestlers registered?
                </label>
                <p class="text-gray-600">
                  Roster will automatically add wrestlers as they register,
                  powered by
                  <a
                    href="https://help.wrestlingiq.com/article/77-automatically-sync-a-roster-with-registrations-and-memberships"
                    class="text-underline"
                    target="_blank"
                  >
                    roster syncers.
                    <i class="fa-solid fa-up-right-from-square" aria-hidden="true"></i>
                  </a>
                </p>
              </div>
            </div>
            <div class="form-group flex align-items-start" v-if="notAnonymous">
              <input
                v-if="!existingSessionId"
                id="sync_more_rosters"
                type="checkbox"
                v-model="syncMoreRosters"
              />
              <div :class="{ 'm-l-sm': !existingSessionId }">
                <label for="sync_more_rosters" class="m-b-none">
                  <span v-if="!existingSessionId">
                    Sync registrants to an existing roster
                  </span>
                  <span v-else>
                    Registrants are synced to these rosters &nbsp;
                  </span>
                  <VDropdown theme="info-tooltip">
                    <!-- This will be the popover target (for the events and position) -->
                    <i class="fa-solid fa-question" v-if="!existingSessionId"></i>
                    <i class="fa-solid fa-circle-info" v-else></i>
                    <!-- This will be the content of the popover -->
                    <template v-slot:popper>
                      <div>
                        <p class="header">
                          Why would I sync to multiple rosters?
                        </p>
                        <p>
                          Some teams like to maintain separate rosters per
                          registration, as well as an annual roster (e.g. 'All
                          Wrestlers 2024-2025'). Check this, box if you want to
                          also sync registrants to an existing roster.
                        </p>
                      </div>
                    </template>
                  </VDropdown>
                </label>
                <p class="text-gray-600">
                  <span v-if="!existingSessionId">
                    In most cases, this will remain unchecked.
                  </span>
                </p>
                <div v-if="syncMoreRosters || existingSessionId">
                  <search
                    class="search-form"
                    response-key="rosters"
                    placeholder="Type the name of a roster to sync..."
                    :mutate-history="false"
                    :allow-add="false"
                    only-dismiss-model="Roster"
                    :small-input="true"
                    :display-all-on-empty="false"
                    readable-model-name="roster"
                    base-url="/api/v1/rosters"
                  >
                    <template v-slot:list="slotProps">
                      <div
                        class="border-top m-t-sm m-b-xs p-t-sm p-b-xs cursor-pointer flex justify-space-between align-items-center p-r-sm"
                        @click="toggleRoster(slotProps.result)"
                      >
                        <div>
                          {{ slotProps.result.name }}
                        </div>
                        <div
                          class="border rounded-sm border-blue-200 border-solid font-small"
                          style="padding: 2px 6px"
                        >
                          Sync
                        </div>
                      </div>
                    </template>
                  </search>
                  <div class="flex flex-wrap gap-x-xxs gap-y-xxs m-t-sm m-b-sm">
                    <button
                      @click="toggleRosterSyncer(rosterSyncer)"
                      class="btn btn-outline rounded-lg font-small"
                      :class="{
                        'btn-success': !rosterSyncer._destroy,
                        'btn-danger': rosterSyncer._destroy,
                      }"
                      style="padding: 2px 6px"
                      v-for="rosterSyncer in additionalRosterSyncers"
                    >
                      {{ rosterSyncer.roster.name }}
                      <i class="fa-solid fa-xmark"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div class="form-group">
              <label class="m-r-sm m-b-none">
                {{
                  teamRegistration
                    ? "Application received email"
                    : "Welcome email"
                }}
                (optional, but recommended)
                <VDropdown theme="info-tooltip">
                  <!-- This will be the popover target (for the events and position) -->
                  <i class="fa-solid fa-question"></i>
                  <!-- This will be the content of the popover -->
                  <template v-slot:popper>
                    <div>
                      <p class="header">
                        Automatically email
                        {{ teamRegistration ? "applicants" : "registrants" }}
                      </p>
                      <p>
                        If you fill this field out, the contents will be
                        automatically emailed to the
                        {{
                          teamRegistration ? "applicants" : "registrants"
                        }}
                        once they successfully
                        {{
                          teamRegistration
                            ? "submit their application"
                            : "register & pay for your session"
                        }}.
                      </p>
                      <p>
                        <a
                          href="https://help.wrestlingiq.com/article/59-automatically-send-an-email-upon-registration"
                          target="_blank"
                          >Read more&nbsp;&nbsp;<i
                            class="fa-solid fa-up-right-from-square"
                            aria-hidden="true"
                          ></i
                        ></a>
                      </p>
                    </div>
                  </template>
                </VDropdown>
              </label>
              <p class="text-gray-600">
                <template v-if="teamRegistration">
                  Use this automation to tell applicants when to expect a
                  response, next steps, etc.
                </template>
                <template v-else>
                  Use this automation to thank registrants and communicate
                  important info, such as facility location.
                </template>
              </p>
              <input id="welcome-editor" type="hidden" name="content" />
              <trix-editor
                input="welcome-editor"
                class="trix-content"
              ></trix-editor>
            </div>
            <div class="form-group" v-show="teamRegistration">
              <label class="m-r-sm m-b-none">
                Approval email custom message (optional)
                <VDropdown theme="info-tooltip">
                  <!-- This will be the popover target (for the events and position) -->
                  <i class="fa-solid fa-question"></i>
                  <!-- This will be the content of the popover -->
                  <template v-slot:popper>
                    <div>
                      <p class="header">
                        Add a custom message to the approval email
                      </p>
                      <p>
                        When you approve an applicant, they receive an email
                        that includes their registration details (division,
                        weight class, and roster assignment). If you add a
                        custom message here, it will be included in that
                        email.
                      </p>
                    </div>
                  </template>
                </VDropdown>
              </label>
              <p class="text-gray-600">
                The approval email already includes the wrestler's division,
                weight class, and roster assignment. Use this field to add a
                custom message with additional details (e.g. practice
                schedule, what to bring, etc).
              </p>
              <input id="approval-editor" type="hidden" name="content" />
              <trix-editor
                input="approval-editor"
                class="trix-content"
              ></trix-editor>
            </div>
            <div class="form-group" v-show="teamRegistration">
              <label class="m-r-sm m-b-none">
                Denial email custom message (optional)
                <VDropdown theme="info-tooltip">
                  <!-- This will be the popover target (for the events and position) -->
                  <i class="fa-solid fa-question"></i>
                  <!-- This will be the content of the popover -->
                  <template v-slot:popper>
                    <div>
                      <p class="header">
                        Add a custom message to the denial email
                      </p>
                      <p>
                        When you deny an applicant, they receive an email
                        notifying them of the decision. If you add a custom
                        message here, it will be included in that email.
                      </p>
                    </div>
                  </template>
                </VDropdown>
              </label>
              <p class="text-gray-600">
                The denial email already includes the wrestler's name and
                registration details. Use this field to add a custom message
                explaining the reason or suggesting alternatives.
              </p>
              <input id="denial-editor" type="hidden" name="content" />
              <trix-editor
                input="denial-editor"
                class="trix-content"
              ></trix-editor>
            </div>
            <div class="form-group" v-show="trial">
              <label class="m-r-sm m-b-none">
                Trial finished email (optional, but recommended)
                <VDropdown theme="info-tooltip">
                  <!-- This will be the popover target (for the events and position) -->
                  <i class="fa-solid fa-question"></i>
                  <!-- This will be the content of the popover -->
                  <template v-slot:popper>
                    <div>
                      <p class="header">
                        Automatically email trial registrants after they have
                        finished their trial.
                      </p>
                      <p>
                        If you fill this field out, the contents will be
                        automatically emailed to the registrants after their
                        final trial pass has been redeemed. You must use
                        WrestlingIQ's attendance tracking feature to redeem
                        passes.
                      </p>
                      <p>
                        <a
                          href="https://help.wrestlingiq.com/article/59-automatically-send-an-email-after-a-trial-has-been-completed"
                          target="_blank"
                          >Read more&nbsp;&nbsp;<i
                            class="fa-solid fa-up-right-from-square"
                            aria-hidden="true"
                          ></i
                        ></a>
                      </p>
                    </div>
                  </template>
                </VDropdown>
              </label>
              <p class="text-gray-600">
                Use this automation to ask how the trial went and provide
                information on how to register for your subscription or full
                season.
              </p>
              <input id="check-in-editor" type="hidden" name="content" />
              <trix-editor
                input="check-in-editor"
                class="trix-content"
              ></trix-editor>
            </div>
          </div>
        </div>
        <div class="row m-t-xl" v-if="!recurring && !anonymous">
          <div class="col-md-4">
            <h1 class="form-section-title m-b-xxs">
              {{ teamRegistration ? "Gear Add-on" : "Custom Upsell / Add-on" }}
            </h1>
            <p class="text-gray-600">Optional</p>
          </div>
          <div class="col-md-8">
            <div class="form-group flex align-items-start">
              <input id="allow_upsell" type="checkbox" v-model="allowUpsell" />
              <div class="m-l-sm">
                <label for="registration_open" class="m-b-none">
                  <template v-if="teamRegistration">
                    Offer a gear add-on during registration (e.g. singlet, team
                    shirt, etc)?
                  </template>
                  <template v-else>
                    Offer an add-on product during registration (e.g. t-shirt,
                    gear package, etc)?
                  </template>
                </label>
                <p class="text-gray-600">
                  <a
                    href="javascript:void(0)"
                    class="cursor-pointer text-underline"
                    onclick="Beacon('article', '6643e10a8f1b9635c99609b5', { type: 'modal' })"
                  >
                    Here is a quick video overview.
                  </a>
                  Skip this if you aren't sure, you can always add this in
                  later.
                </p>
              </div>
            </div>
            <div v-if="allowUpsell" class="m-l-md">
              <p v-if="!upsellProduct">No product selected yet.</p>
              <div
                v-if="upsellProduct"
                class="grid grid-cols-5 sm-grid-cols-1 md-grid-cols-2 gap-sm m-b-sm"
              >
                <div>
                  <product-list-result
                    :product="upsellProduct"
                    :show-edit="false"
                  ></product-list-result>
                  <div
                    @click="upsellProduct = null"
                    class="m-t-xxs w-full inline-flex align-items-center gap-x-xs justify-center rounded-xxs font-small p-x-xs p-y-xxs border border-solid border-red-500 text-red-600 rounded-full cursor-pointer"
                  >
                    <i class="fa-solid fa-xmark"></i><span>Remove product</span>
                  </div>
                </div>
              </div>
              <div
                class="form-group flex align-items-start m-t-sm"
                v-if="upsellProduct"
              >
                <input
                  id="upsell_required"
                  type="checkbox"
                  v-model="upsellRequired"
                />
                <div class="m-l-sm">
                  <label for="upsell_required" class="m-b-none">
                    Require this add-on during registration?
                  </label>
                  <p class="text-gray-600">
                    If checked, registrants must purchase this add-on to
                    complete registration.
                  </p>
                </div>
              </div>
              <div v-if="!upsellProduct">
                <hr />
                <label class="m-b-none"
                  >Product library. Tap to select the product you want to offer
                  as an add-on for this registration.</label
                >
                <p class="">
                  Not seeing anything?
                  <a href="/products" target="_blank" class="text-underline"
                    >Create some products first.
                    <i class="fa-solid fa-up-right-from-square"></i
                  ></a>
                </p>
                <filtered-list
                  ui="grid"
                  grid-classes="grid grid-cols-5 sm-grid-cols-1 md-grid-cols-2 gap-sm m-b-sm"
                  :headers="['Name', 'Category', 'Actions']"
                  :sorts="[
                    {
                      label: 'Name asc',
                      value: 'name asc',
                    },
                    {
                      label: 'Name desc',
                      value: 'name desc',
                    },
                  ]"
                  :filters="[
                    {
                      label: 'Type',
                      attribute: 'product_type',
                      type: 'primary',
                      predicates: [
                        {
                          label: 'All Products',
                          user_editable: false,
                          default: true,
                          values: {
                            product_type_eq: 'good',
                          },
                        },
                      ],
                    },
                    {
                      label: 'Name',
                      predicateLabel: 'Filter by product name',
                      attribute: 'name',
                      type: 'secondary',
                      predicates: [
                        {
                          id: 1,
                          label: 'Name contains',
                          type: 'TextContains',
                          user_editable: true,
                          operator: 'or',
                          placeholder: 'Product name',
                          values: {
                            name_cont_any: null,
                          },
                        },
                      ],
                    },
                  ]"
                  response-key="products"
                  object-type="Product"
                  base-url="/api/v1/products"
                >
                  <template v-slot:row="row">
                    <div @click.prevent="upsellProduct = row.data">
                      <product-list-result
                        :product="row.data"
                        :show-edit="false"
                      ></product-list-result>
                    </div>
                  </template>
                </filtered-list>
              </div>
            </div>
          </div>
        </div>
        <div class="button-area text-right m-t-xl">
          <div v-show="showSaveButton">
            <div v-show="error">
              <small>
                <span class="text-danger">{{ errorMessage }}</span>
              </small>
            </div>
            <ladda-button
              @lbClicked="pingServer"
              el-class="btn-primary"
              :loading="loading"
            >
              Save
            </ladda-button>
          </div>
          <div v-show="!showSaveButton">
            <small>Finish editing your pricing to save the session.</small>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import Spinner from './spinner.vue';
import LaddaButton from '../../shared/ladda_button.vue';
import PaymentOptionForm from './payment_option_form.vue';
import SubscriptionPriceForm from './subscription_price_form.vue';
import DatePairPicker from './date_pair_picker.vue';
import ProductListResult from './product_list_result.vue';
import FilteredList from './filtered_list.vue';
import { trixFileAttachmentMix } from './mixins/trix_file_attachment_mix';
import Search from '../../shared/search.vue';

export default {
  name: 'paid-session-form',
  components: {
    DatePairPicker,
    FilteredList,
    LaddaButton,
    PaymentOptionForm,
    ProductListResult,
    Search,
    Spinner,
    SubscriptionPriceForm,
  },
  mixins: [trixFileAttachmentMix],
  props: {
    teamId: {
      type: Number,
      required: true,
    },
    existingSessionId: {
      type: Number,
      required: false,
    },
    membership: {
      type: Boolean,
      default: false,
    },
    // Drives the Private Lessons card in the type picker: open the dashboard when
    // the team has the feature on, otherwise send them to the settings toggle.
    privateLessonsEnabled: {
      type: Boolean,
      default: false,
    },
    replicateSession: {
      type: Object,
      default() {
        return {};
      },
    },
    accessKey: {
      type: String,
      required: false,
    },
    authToken: {
      type: String,
      required: false,
    },
    cloudfrontBaseUrl: {
      type: String,
      required: false,
    },
    endpoint: {
      type: String,
      required: false,
    },
    currentBillingPartner: {
      type: String,
      required: true,
    },
    currency: {
      type: String,
      default: 'usd',
    },
    teamUsawMemberships: {
      type: String,
      required: false,
    },
    teamAauMemberships: {
      type: String,
      required: false,
    },
    currentProfileId: {
      type: Number,
      required: false,
      default: null,
    },
    replicateMessageGroupCoaches: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      loading: false,
      error: false,
      errorMessage: 'Error, please try again later or contact support',

      paymentOptionsBeingEdited: 0,
      subOptionsBeingEdited: 0,
      // Monotonic client-side id for draft payment options / subscription
      // prices. Never reused, so it is safe to use as a v-for :key and for the
      // findIndex lookups that reconcile drafts. (Previously derived from
      // array.length + 1, which collided after a removal and matched the wrong
      // row.)
      newOptionIdCounter: 0,

      loadingExisting: false,
      name: '',
      sessionId: null,
      registrationOpen: true,
      showOnTeamRegPage: true,
      excludeFromGSibDiscount: false,
      allowMultipleSignupsPerWrestler: false,
      createRoster: true,
      syncMoreRosters: false,
      additionalRosterSyncers: [], // Array of roster syncers
      allCoachesInMessageGroup: true,
      // Coaches the user has explicitly added to the message group (excludes
      // the current user, who is always implicitly included and rendered as
      // a pinned, non-removable chip). Shape: [{ id, full_name }, ...]
      selectedCoaches: [],
      showInsurance: true,
      highlightInsuranceValue: false,
      participants: 'kids',
      sessionType: null,
      // Cards for the "What are you setting up registration for?" picker. accountLabel
      // mirrors PaidSession#readable_wiq_account_type; camp/clinic and drop in defer
      // the full-vs-guest choice to the account picker on the next step.
      sessionTypeCards: [
        {
          value: 'full',
          name: 'Season',
          blurb: 'Set start and end dates. Charge once or offer a payment plan to charge installments.',
          accountLabel: 'Full WrestlingIQ accounts',
          accountBadge: 'badge-green',
          chips: ['Payment plans'],
          gets: [
            { icon: 'yes', text: 'WrestlingIQ login and mobile app' },
            { icon: 'yes', text: 'All WrestlingIQ features such as messaging, schedule, and billing' },
          ],
          usedFor: [
            'Seasonal registration (Folkstyle/Freestyle)',
          ],
          articleId: '650cd0d8aed9c75bab283301',
          showGets: false,
          showUsedFor: false,
        },
        {
          value: 'guest',
          name: 'Camp / Clinic',
          blurb: 'Fast checkout for camps. Returning families are pre-filled.',
          accountLabel: 'Full accounts or guest checkout',
          accountBadge: 'badge-blue',
          chips: ['Payment plans'],
          gets: [
            { icon: 'optional', text: 'You pick one on the next step' },
            { icon: 'yes', text: 'Full: login, mobile app, in-app messaging' },
            { icon: 'yes', text: 'Guest: no password, records created, email-only messaging' },
            { icon: 'yes', text: 'Pre-filled forms for returning families either way' },
          ],
          usedFor: ['Summer clinic', 'Multi-day or multi-week camp'],
          articleId: '650dc66aaed9c75bab2835e9',
          showGets: false,
          showUsedFor: false,
        },
        {
          value: 'recurring',
          name: 'Recurring Subscription',
          blurb: 'Bill monthly, every two weeks, or annually like a gym. Optional contract lengths.',
          accountLabel: 'Full WrestlingIQ accounts',
          accountBadge: 'badge-green',
          chips: ['Recurring billing'],
          gets: [
            { icon: 'yes', text: 'WrestlingIQ login and mobile app' },
            { icon: 'yes', text: 'All WrestlingIQ features such as messaging, schedule, and billing' },
          ],
          usedFor: [
            'Clubs that bill monthly instead of by season',
            '3, 6, or 12 month contract tiers',
          ],
          articleId: '650dc6c8e7ab134c25de1ab0',
          showGets: false,
          showUsedFor: false,
        },
        {
          value: 'dropin',
          name: 'Drop In',
          blurb: 'Sell single class passes or packs. E.g. 1 class or a pack of 10.',
          accountLabel: 'Full accounts or guest checkout',
          accountBadge: 'badge-blue',
          chips: ['Class passes'],
          gets: [
            { icon: 'optional', text: 'You pick one on the next step' },
            { icon: 'yes', text: 'Full: login, mobile app, remaining passes visible in the app' },
            { icon: 'yes', text: 'Guest: no password, records created, email-only messaging' },
            { icon: 'yes', text: 'Pre-filled forms for returning families either way' },
          ],
          usedFor: ['Drop in wrestlers', 'Open mat nights', 'Visiting wrestlers', 'Punch cards'],
          articleId: '650dc733e7ab134c25de1ab3',
          showGets: false,
          showUsedFor: false,
        },
        {
          value: 'trial',
          name: 'Trial',
          blurb: 'A limited number of classes for prospects. Pairs well with subscriptions.',
          accountLabel: 'Full WrestlingIQ accounts',
          accountBadge: 'badge-green',
          chips: ['Class passes'],
          gets: [
            { icon: 'yes', text: 'WrestlingIQ login and mobile app' },
            { icon: 'yes', text: 'All WrestlingIQ features such as messaging, schedule, and billing' },
            { icon: 'yes', text: 'Registrants can see their remaining class passes' },
          ],
          usedFor: ['Free or paid intro class', 'Multi-day trials'],
          articleId: '66340e0d0d8e3e7142b0f36a',
          showGets: false,
          showUsedFor: false,
        },
        {
          value: 'team_registration',
          name: 'Travel / Dual Team',
          blurb: 'Collect applications to build rosters by age and weight. Then finalize roster to charge families.',
          accountLabel: 'Full WrestlingIQ accounts',
          accountBadge: 'badge-green',
          chips: ['Payment plans'],
          gets: [
            { icon: 'yes', text: 'WrestlingIQ login and mobile app' },
            { icon: 'yes', text: 'All WrestlingIQ features such as messaging, schedule, and billing' },
          ],
          usedFor: [
            'Travel team registration',
            'Team dual tournaments',
            'Age or weight division based team selection',
          ],
          articleId: '69cd43eeb1c5f7a6f3ae8a5a',
          showGets: false,
          showUsedFor: false,
        },
        // Link-out cards. These are not paid session types; they send the coach to
        // the feature that actually fits, so the picker covers every "I want to
        // charge for X" question coaches show up with.
        {
          value: 'private_lessons',
          link: 'private_lessons',
          name: 'Private Lessons',
          blurb: 'Set coach rates and availability. Families book and pay per lesson.',
          accountLabel: 'Full WrestlingIQ accounts',
          accountBadge: 'badge-green',
          chips: ['Per-lesson booking', 'Coach payroll'],
          gets: [
            { icon: 'yes', text: 'Book a specific coach from the WIQ app' },
            { icon: 'yes', text: 'Card required at booking, charged after lesson completes' },
          ],
          usedFor: ['One-on-one coaching', 'Small group lessons'],
          articleId: '6846098ca0fc382125f8525e',
          showGets: false,
          showUsedFor: false,
        },
        {
          value: 'ticketed_event',
          link: 'online_stores',
          name: 'Ticketed Event',
          blurb: 'Sell tickets for banquets, spectators, or other team events through an online store.',
          accountLabel: 'No accounts',
          accountBadge: 'badge-gray',
          chips: ['Online store'],
          gets: [
            { icon: 'yes', text: 'Pick a quantity and pay in one step' },
            { icon: 'yes', text: 'Orders tracked in your online store' },
            { icon: 'no', text: 'No WrestlingIQ login' },
          ],
          usedFor: ['Banquet tickets', 'Spectator admission', 'Fundraiser dinners'],
          articleId: '6643e10a8f1b9635c99609b5',
          helpLabel: 'Learn about online stores',
          showGets: false,
          showUsedFor: false,
        },
        {
          value: 'gear',
          link: 'gear',
          name: 'Gear',
          blurb: 'Sell team apparel and equipment through an online store.',
          accountLabel: 'No accounts',
          accountBadge: 'badge-gray',
          chips: ['Online store'],
          teaser: 'WIQ Gear: pick a custom gear package and we run the store, handle sales tax, ship to your club and send you the fundraising dollars.',
          teaserLink: '/gear#gear-packages',
          gets: [
            { icon: 'yes', text: 'Product listings with your own prices' },
            { icon: 'yes', text: 'Orders tracked in your online store' },
            { icon: 'no', text: 'No WrestlingIQ login' },
          ],
          usedFor: ['Team apparel', 'Headgear and shoes', 'Spirit wear for families'],
          articleId: '67c8b1fbde0e7e2fa1eb1f47',
          helpLabel: 'Learn more',
          showGets: false,
          showUsedFor: false,
        },
        {
          value: 'anonymous',
          name: 'Other',
          blurb: 'A simple payment form for anything that does not fit above.',
          accountLabel: 'No accounts',
          accountBadge: 'badge-gray',
          chips: ['No rosters'],
          gets: [
            { icon: 'yes', text: 'Simple payment form' },
            { icon: 'no', text: 'No WrestlingIQ login' },
            { icon: 'no', text: 'No parent or wrestler records created' },
            { icon: 'no', text: 'Only collects email and name' },
          ],
          usedFor: ['One-off fees or deposits', 'Anything that does not need a roster'],
          articleId: '650dc79b6a7bf14d5d3c51fe',
          showGets: false,
          showUsedFor: false,
        },
      ],
      // Account axis: 'full' or 'guest'. Only chosen (and only sent) for camp/clinic
      // and drop in types; every other type is inferred server-side. No default on
      // purpose — the coach must make an explicit choice before saving.
      wiqAccountType: null,
      // True once selectCard has pushed a history entry, so popstate knows the
      // picker is a valid place to return to on this page load.
      pickerHistoryPushed: false,
      capacityLimit: 50,
      hasCapacityLimit: false,
      start: null,
      end: null,
      welcomeText: '',
      welcomeTrixEditor: null,
      checkInText: '',
      checkInTrixEditor: null,
      approvalText: '',
      approvalTrixEditor: null,
      denialText: '',
      denialTrixEditor: null,
      allowUpsell: false,
      upsellProduct: null,
      upsellRequired: false,
      usawOverride: null,
      aauOverride: null,
      showAdditionalSettings: false,

      // Division standards for team registration
      divisionStandards: [],
      selectedDivisionStandardId: null,
      loadingDivisionStandards: false,
      enabledAgeDivisionIds: [],
      existingTeamRegistrationDivisions: [],

      // Locations
      locations: [],
      selectedLocationId: null,
      loadingLocations: false,

      paymentOptions: [],
      subscriptionPrices: [],

      paymentOptionId: null, // TEMP
      currentYear: new Date().getFullYear().toString(),

      newlyCreatedSessionId: null,

      // Set to true if we had to null out any past-dated installments while
      // loading a replicated session, so the review banner can surface an
      // extra-loud warning.
      replicateInstallmentDatesCleared: false,

      linkedDocumentIds: [],
      availableDocuments: [],
      linkedDocsDirty: false,
      useSpecificDocuments: false,
    };
  },
  computed: {
    insuranceAlertClass() {
      return {
        border:
          this.highlightInsuranceValue || !this.billingPartnerAllowsInsurance,
        'border-solid':
          this.highlightInsuranceValue || !this.billingPartnerAllowsInsurance,
        'border-gray-50': !this.billingPartnerAllowsInsurance,
        'border-green-50': this.highlightInsuranceValue,
        'm-b-sm':
          this.highlightInsuranceValue || !this.billingPartnerAllowsInsurance,
      };
    },
    insuranceBodyClass() {
      return {
        'bg-green-50':
          this.highlightInsuranceValue && this.billingPartnerAllowsInsurance,
        'bg-gray-50': !this.billingPartnerAllowsInsurance,
        'opacity-50': !this.billingPartnerAllowsInsurance,
        'p-t-sm':
          this.highlightInsuranceValue || !this.billingPartnerAllowsInsurance,
        'p-l-m':
          this.highlightInsuranceValue || !this.billingPartnerAllowsInsurance,
        'm-b-none':
          this.highlightInsuranceValue || !this.billingPartnerAllowsInsurance,
      };
    },
    billingPartnerAllowsInsurance() {
      return this.currentBillingPartner === 'justifi';
    },
    hasAdditionalSettings() {
      // Always true — the "hide from team registration page" toggle applies to every session type.
      return true;
    },
    hideFromTeamRegPage: {
      get() {
        return !this.showOnTeamRegPage;
      },
      set(value) {
        this.showOnTeamRegPage = !value;
      },
    },
    teamHasUsawRequired() {
      return (
        this.teamUsawMemberships
        && [
          'usaw_require_id_and_expires',
          'usaw_require_all',
          'usaw_optional',
        ].includes(this.teamUsawMemberships)
      );
    },
    teamHasAauRequired() {
      return (
        this.teamAauMemberships
        && [
          'aau_require_id_and_expires',
          'aau_require_all',
          'aau_optional',
        ].includes(this.teamAauMemberships)
      );
    },
    bypassUsawCollection: {
      get() {
        return this.usawOverride === 'usaw_disabled';
      },
      set(value) {
        this.usawOverride = value ? 'usaw_disabled' : null;
      },
    },
    bypassAauCollection: {
      get() {
        return this.aauOverride === 'aau_disabled';
      },
      set(value) {
        this.aauOverride = value ? 'aau_disabled' : null;
      },
    },
    replicatingSession() {
      return !_.isEmpty(this.replicateSession);
    },
    capacityLimitParam() {
      // Some safeguards for situatinos where registration limits do not yet apply
      if (this.anonymous || this.recurring || this.dropin) {
        return null;
      }

      if (this.hasCapacityLimit && this.capacityLimit > 0) {
        return this.capacityLimit;
      }

      return null;
    },
    showSaveButton() {
      return (
        this.paymentOptionsBeingEdited === 0 && this.subOptionsBeingEdited === 0
      );
    },
    // Fee, tax, and one-time rows live in subscriptionPrices too but are not
    // shown as pickable prices, so only count the ones the coach actually sees.
    hasSubscriptionPrice() {
      return this.subscriptionPrices.some(
        (p) => !p.is_wiq_fee && !p.is_tax_item && !p.is_one_time_fee,
      );
    },
    anonymous() {
      return !this.notAnonymous;
    },
    notAnonymous() {
      return this.sessionType !== 'anonymous';
    },
    recurring() {
      return this.sessionType === 'recurring';
    },
    dropin() {
      return this.sessionType === 'dropin';
    },
    trial() {
      return this.sessionType === 'trial';
    },
    campOrClinic() {
      return this.sessionType === 'guest';
    },
    accountChoiceRequired() {
      return this.campOrClinic || this.dropin;
    },
    fullSeason() {
      return this.sessionType === 'full';
    },
    teamRegistration() {
      return this.sessionType === 'team_registration';
    },
    selectedDivisionStandard() {
      if (!this.selectedDivisionStandardId) return null;
      return this.divisionStandards.find(
        (s) => s.id === this.selectedDivisionStandardId,
      );
    },
    // Archived locations stay selectable only when the session is already assigned to one
    selectableLocations() {
      return this.locations.filter(
        (l) => !l.archived || l.id === this.selectedLocationId,
      );
    },
    requireDates() {
      return !(this.dropin || this.recurring || this.trial);
    },
    dateLabelText() {
      if (this.campOrClinic) {
        return 'Camp / Clinic';
      } if (this.fullSeason) {
        return 'Season';
      } if (this.teamRegistration) {
        return 'Team event';
      }
      return '';
    },
    dateLabelTextLower() {
      return this.dateLabelText.toLowerCase();
    },
    sessionTypeSet() {
      return this.sessionType !== null;
    },
  },
  watch: {
    createRoster(val) {
      if (!val) {
        this.allCoachesInMessageGroup = true;
        this.selectedCoaches = [];
      }
    },
    allCoachesInMessageGroup(val) {
      if (val) {
        this.selectedCoaches = [];
      }
    },
  },
  mounted() {
    const vm = this;
    window.addEventListener('popstate', vm.handlePopState);

    if (this.membership) {
      this.sessionType = 'recurring';
    }

    if (!this.billingPartnerAllowsInsurance) {
      this.showInsurance = false;
    }

    if (vm.existingSessionId) {
      // Pull an existing session from the server if we are editing it
      vm.getExisting();
    } else if (this.replicatingSession) {
      // Use the replicate session if given
      vm.setExistingOrReplicateData(vm.replicateSession, true);
    }

    // Fetch division standards for team registration
    vm.fetchDivisionStandards();
    vm.fetchLocations();
    // Fetch available documents
    vm.fetchAvailableDocuments();
    document.addEventListener('trix-initialize', vm.trixInit);
    // Registration session types
    vm.$on('editing-payment-option', vm.incrementEditing);
    vm.$on('editing-payment-option-cancelled', vm.handleCancelledOption);
    vm.$on('discard-payment-option-draft', vm.removeDraft);
    vm.$on('payment-option-updated', vm.handleUpdatedOption);

    // Membership types
    vm.$on('editing-subscription-option', vm.incrementSubEditing);
    vm.$on(
      'editing-subscription-option-cancelled',
      vm.handleCancelledSubOption,
    );
    vm.$on('discard-subscription-option-draft', vm.removeSubDraft);
    vm.$on('subscription-option-updated', vm.handleUpdatedSubOption);
  },
  destroyed() {
    const vm = this;
    document.removeEventListener('trix-initialize', vm.trixInit);
    window.removeEventListener('popstate', vm.handlePopState);

    // registration session types
    vm.$off('editing-payment-option', vm.incrementEditing);
    vm.$off('editing-payment-option-cancelled', vm.handleCancelledOption);
    vm.$off('discard-payment-option-draft', vm.removeDraft);
    vm.$off('payment-option-updated', vm.handleUpdatedOption);

    // membership types
    vm.$off('editing-subscription-option', vm.incrementSubEditing);
    vm.$off(
      'editing-subscription-option-cancelled',
      vm.handleCancelledSubOption,
    );
    vm.$off('discard-subscription-option-draft', vm.removeSubDraft);
    vm.$off('subscription-option-updated', vm.handleUpdatedSubOption);
  },

  methods: {
    openHelpArticle(articleId) {
      window.Beacon('article', articleId, { type: 'modal' });
    },
    // Link cards navigate to the feature that fits; type cards start the form.
    selectCard(card) {
      const href = this.cardHref(card);
      if (href) {
        window.location.assign(href);
        return;
      }
      // Push a history entry so the browser back button returns to the picker
      // instead of leaving the page. Only for brand new sessions: edit, replicate,
      // and membership flows arrive with a type already set and never see the picker.
      if (!this.existingSessionId && window.history && window.history.pushState) {
        window.history.pushState({ paidSessionType: card.value }, '');
        this.pickerHistoryPushed = true;
      }
      this.sessionType = card.value;
      this.scrollToTop();
    },
    // The picker is tall on phones, so swapping it for the form leaves the
    // viewport partway down the page. Jump to the top after either transition.
    scrollToTop() {
      this.$nextTick(() => window.scrollTo(0, 0));
    },
    handlePopState(event) {
      if (!this.pickerHistoryPushed) return;
      const restoredType = event.state && event.state.paidSessionType;
      if (restoredType) {
        // Forward button after going back: land on the type they had picked
        this.sessionType = restoredType;
        this.scrollToTop();
        return;
      }
      // Back to the picker. Clear the account choice too so a wrong tap on
      // camp/drop in does not leak a full-vs-guest answer into the next type.
      this.sessionType = null;
      this.wiqAccountType = null;
      this.scrollToTop();
    },
    cardHref(card) {
      if (card.link === 'online_stores') return '/online_stores';
      if (card.link === 'gear') return '/gear';
      if (card.link === 'private_lessons') {
        return this.privateLessonsEnabled
          ? '/private_lessons/dashboard'
          : '/settings/team/private_lessons';
      }
      return null;
    },
    cardCta(card) {
      if (card.link === 'online_stores') return 'Open Online Stores';
      if (card.link === 'gear') return 'Open WIQ Gear';
      if (card.link === 'private_lessons') {
        return this.privateLessonsEnabled
          ? 'Open Private Lessons'
          : 'Turn on Private Lessons';
      }
      return `Create ${card.name}`;
    },
    registrantIconClass(icon) {
      switch (icon) {
        case 'yes':
          return 'fa-solid fa-check text-green-600';
        case 'no':
          return 'fa-solid fa-xmark text-red-600';
        case 'warn':
          return 'fa-solid fa-triangle-exclamation text-yellow-600';
        default:
          return 'fa-solid fa-minus text-blue-600';
      }
    },
    fetchDivisionStandards() {
      const vm = this;
      vm.loadingDivisionStandards = true;
      const url = vm.$apiService.divisionStandardsUrl();
      axios
        .get(url)
        .then((response) => {
          vm.divisionStandards = response.data.division_standards;
          vm.loadingDivisionStandards = false;
        })
        .catch((error) => {
          // Error fetching division standards - fail silently
          vm.loadingDivisionStandards = false;
        });
    },
    fetchLocations() {
      const vm = this;
      vm.loadingLocations = true;
      const url = `${vm.$apiService.locationsUrl()}?include_archived=true`;
      axios
        .get(url)
        .then((response) => {
          vm.locations = response.data.locations;
          vm.loadingLocations = false;
        })
        .catch((error) => {
          // Error fetching locations - fail silently
          vm.loadingLocations = false;
        });
    },
    onDivisionStandardChange() {
      // When division standard changes, select all divisions by default
      // and clear existing records since they belong to the old standard
      this.existingTeamRegistrationDivisions = [];
      if (this.selectedDivisionStandard) {
        this.enabledAgeDivisionIds = this.selectedDivisionStandard.age_divisions.map((d) => d.id);
      } else {
        this.enabledAgeDivisionIds = [];
      }
    },
    selectAllDivisions() {
      if (this.selectedDivisionStandard) {
        this.enabledAgeDivisionIds = this.selectedDivisionStandard.age_divisions.map((d) => d.id);
      }
    },
    deselectAllDivisions() {
      this.enabledAgeDivisionIds = [];
    },
    buildTeamRegistrationDivisionsAttributes() {
      const attributes = [];
      // Mark existing records for destruction if no longer enabled
      this.existingTeamRegistrationDivisions.forEach((trd) => {
        if (!this.enabledAgeDivisionIds.includes(trd.age_division_id)) {
          attributes.push({ id: trd.id, _destroy: true });
        }
      });
      // Add new divisions that don't have existing records
      const existingDivisionIds = this.existingTeamRegistrationDivisions.map((trd) => trd.age_division_id);
      this.enabledAgeDivisionIds.forEach((divisionId) => {
        if (!existingDivisionIds.includes(divisionId)) {
          attributes.push({ age_division_id: divisionId, enabled: true });
        }
      });
      return attributes;
    },
    formatWeightClasses(weightClasses) {
      if (!weightClasses || weightClasses.length === 0) return '';
      const weights = weightClasses.map((wc) => wc.weight_value);
      weights.sort((a, b) => a - b);
      return `${weights.join(', ')} lbs`;
    },
    isCoachSelected(coach) {
      return (
        coach.id === this.currentProfileId
        || _.some(this.selectedCoaches, { id: coach.id })
      );
    },
    addCoach(coach) {
      // Current user is always implicitly included via the pinned chip — no
      // need to track them in selectedCoaches.
      if (coach.id === this.currentProfileId) {
        return;
      }
      if (_.some(this.selectedCoaches, { id: coach.id })) {
        return;
      }
      this.selectedCoaches.push({ id: coach.id, full_name: coach.full_name });
      // Dismiss the Search dropdown so the chip becomes the visible feedback.
      // Matches the pattern used by toggleRoster below.
      this.$notificationManager.emit('profile-clicked', 'CoachProfile');
    },
    removeCoach(coach) {
      const idx = _.findIndex(this.selectedCoaches, { id: coach.id });
      if (idx !== -1) {
        this.selectedCoaches.splice(idx, 1);
      }
    },
    toggleRosterSyncer(rosterSyncer) {
      // Roster syncers come in a payload of { id: roster_syncer.id, roster_id: roster.id } or a nested one to create a new roster
      const rosterIdx = _.findIndex(this.additionalRosterSyncers, {
        id: rosterSyncer.id,
      });
      if (rosterIdx === -1) {
        rosterSyncer._destroy = false;
        this.additionalRosterSyncers.push(rosterSyncer);
      } else {
        const syncer = this.additionalRosterSyncers[rosterIdx];
        if (syncer.id) {
          syncer._destroy = !syncer._destroy;
        } else {
          this.additionalRosterSyncers.splice(rosterIdx, 1);
        }
      }
      this.$notificationManager.emit('profile-clicked', 'Roster');
    },
    toggleRoster(roster) {
      const rosterIdx = _.findIndex(this.additionalRosterSyncers, {
        roster_id: roster.id,
      });
      if (rosterIdx === -1) {
        this.additionalRosterSyncers.push({
          id: null,
          _destroy: false,
          roster_id: roster.id,
          roster,
        });
      } else {
        const syncer = this.additionalRosterSyncers[rosterIdx];
        if (syncer.id) {
          syncer._destroy = !syncer._destroy;
        } else {
          this.additionalRosterSyncers.splice(rosterIdx, 1);
        }
      }
      this.$notificationManager.emit('profile-clicked', 'Roster');
    },
    trixInit(event) {
      const vm = this;
      if (event.target.inputElement.id === 'check-in-editor') {
        vm.checkInTrixEditor = event.target.editor;
        vm.loadTrixContent(vm.checkInTrixEditor, vm.checkInText);
      } else if (event.target.inputElement.id === 'welcome-editor') {
        vm.welcomeTextTrixEditor = event.target.editor;
        vm.loadTrixContent(vm.welcomeTextTrixEditor, vm.welcomeText);
      } else if (event.target.inputElement.id === 'approval-editor') {
        vm.approvalTrixEditor = event.target.editor;
        vm.loadTrixContent(vm.approvalTrixEditor, vm.approvalText);
      } else if (event.target.inputElement.id === 'denial-editor') {
        vm.denialTrixEditor = event.target.editor;
        vm.loadTrixContent(vm.denialTrixEditor, vm.denialText);
      }
    },
    nextNewOptionId() {
      this.newOptionIdCounter += 1;
      return this.newOptionIdCounter;
    },
    incrementEditing() {
      this.paymentOptionsBeingEdited += 1;
    },
    decrementEditing() {
      this.paymentOptionsBeingEdited -= 1;
    },
    handleCancelledOption(newOptionId, shouldRemove) {
      if (newOptionId && shouldRemove) {
        const newOptionIdx = _.findIndex(this.paymentOptions, { newOptionId });
        this.paymentOptions.splice(newOptionIdx, 1);
      }
      this.decrementEditing();
    },
    removeDraft(newOptionId) {
      const newOptionIdx = _.findIndex(this.paymentOptions, { newOptionId });
      this.paymentOptions.splice(newOptionIdx, 1);
    },
    handleUpdatedOption(option) {
      const vm = this;
      if (option.id) {
        // this option already exists so replace it in paymentOptions
        const existingOptionIdx = _.findIndex(vm.paymentOptions, {
          id: option.id,
        });
        vm.paymentOptions.splice(existingOptionIdx, 1, option);
      } else {
        // option does not already exist, find it's draft option or push
        const newOptionIdx = _.findIndex(vm.paymentOptions, {
          newOptionId: option.newOptionId,
        });
        vm.paymentOptions.splice(newOptionIdx, 1, option);
      }

      this.decrementEditing();
    },
    addNewOption() {
      // ok so this needs to throw down a new payment option form
      // and open it for editing
      const newOptionId = this.nextNewOptionId();
      const newPaymentOption = {
        draft: true,
        id: null,
        newOptionId,
        name: '',
        price: 0,
        status: 'active',
        sibling_discount_enabled: false,
        class_pass_quantity: null,
        discount_per_extra_wrestler: 0,
        sibling_count_for_discount: 1,
        allowed_payment_methods: 'card',
      };
      this.paymentOptions.push(newPaymentOption);
    },
    incrementSubEditing() {
      this.subOptionsBeingEdited += 1;
    },
    decrementSubEditing() {
      this.subOptionsBeingEdited -= 1;
    },
    handleCancelledSubOption(newOptionId, shouldRemove) {
      if (newOptionId && shouldRemove) {
        const newOptionIdx = _.findIndex(this.subscriptionPrices, {
          newOptionId,
        });
        this.subscriptionPrices.splice(newOptionIdx, 1);
      }
      this.decrementSubEditing();
    },
    removeSubDraft(newOptionId) {
      const newOptionIdx = _.findIndex(this.subscriptionPrices, {
        newOptionId,
      });
      this.subscriptionPrices.splice(newOptionIdx, 1);
    },
    handleUpdatedSubOption(option) {
      const vm = this;
      if (option.id) {
        // this option already exists so replace it in paymentOptions
        const existingOptionIdx = _.findIndex(vm.subscriptionPrices, {
          id: option.id,
        });
        vm.subscriptionPrices.splice(existingOptionIdx, 1, option);
      } else {
        // option does not already exist, find it's draft option or push
        const newOptionIdx = _.findIndex(vm.subscriptionPrices, {
          newOptionId: option.newOptionId,
        });
        vm.subscriptionPrices.splice(newOptionIdx, 1, option);
      }

      this.decrementSubEditing();
    },
    addNewSubPrice() {
      // Throw down a new sub price, open it for editing
      const newOptionId = this.nextNewOptionId();
      const newSubPrice = {
        draft: true,
        id: null,
        newOptionId,
        name: '',
        amount: 0,
        currency: this.currency,
        interval: 'month',
        interval_count: 1,
        intervals_to_bill: null,
        sibling_coupon_id: null,
        anchor_day_of_month: null,
        billing_starts_at: null,
        third_sibling_coupon_id: null,
        one_time_fee_amount: 0,
      };
      this.subscriptionPrices.push(newSubPrice);
    },
    loadTrixContent(trixEditor, html) {
      console.log('trix init');
      if (trixEditor && html) {
        trixEditor.loadHTML(html);
      }
    },
    // Useful for setting everything - except the session id - in different contexts.
    setExistingOrReplicateData(data, isReplicating) {
      this.name = data.name;
      this.registrationOpen = data.registration_open;
      this.showOnTeamRegPage = data.show_on_team_reg_page !== false;
      this.showInsurance = data.show_insurance;
      this.highlightInsuranceValue = data.show_insurance === false
        && moment(data.created_at).isBefore('2024-03-06');
      this.excludeFromGSibDiscount = data.exclude_from_g_sib_discount;
      this.allowMultipleSignupsPerWrestler = data.allow_multiple_signups_per_wrestler;
      this.usawOverride = data.usaw_override;
      this.aauOverride = data.aau_override;
      this.selectedDivisionStandardId = data.division_standard_id;
      this.selectedLocationId = data.location_id || null;
      this.enabledAgeDivisionIds = _.uniq(data.enabled_age_division_ids || []);
      // When replicating, treat as a fresh session: ignore the old session's TRD ids so that
      // buildTeamRegistrationDivisionsAttributes emits new {age_division_id, enabled} entries
      // rather than {id, _destroy} attributes referencing rows on the source session (which 404).
      this.existingTeamRegistrationDivisions = isReplicating ? [] : (data.team_registration_divisions || []);
      this.sessionType = data.session_type;
      // Replicated sessions must NOT inherit the account type — reset it so the
      // coach is forced to make an explicit choice for the new session.
      this.wiqAccountType = isReplicating ? null : data.wiq_account_type || null;
      this.capacityLimit = data.capacity_limit;
      // If we receive null, we need to reset this so the number model works still (it does not accept null without throwing a front end error)
      // We rely on the hasCapacityLimit flag to properly handle not sending this if it isn't checked, no matter what this number is
      if (this.capacityLimit == null) {
        this.capacityLimit = 50;
      } else {
        // If it's not null, we do have a limit so make sure that is set here!
        this.hasCapacityLimit = true;
      }

      this.upsellProduct = data.upsell_product;
      this.allowUpsell = this.upsellProduct !== null;
      this.upsellRequired = data.upsell_required || false;
      this.participants = data.participants;
      // Carry forward the original session's message-group coach choices when
      // replicating so the coach doesn't silently lose a hand-curated list.
      // For editing an existing session the coach UI is hidden, so this is
      // effectively replicate-only state. The coach names come in via a
      // separate prop (denormalized server-side) to avoid an N+1 on the
      // shared paid_session jbuilder.
      if (isReplicating) {
        if (typeof data.all_coaches_in_message_group === 'boolean') {
          this.allCoachesInMessageGroup = data.all_coaches_in_message_group;
        }
        this.selectedCoaches = (this.replicateMessageGroupCoaches || [])
          .filter((c) => c.id !== this.currentProfileId)
          .map((c) => ({ id: c.id, full_name: c.full_name }));
        // Carry forward the source session's scoped document picks. Pre-
        // scoped-docs this was a safe no-op because team-wide auto-attach
        // covered everything; now a replicated session that drops its picks
        // can silently lose a one-off doc (e.g. a camp waiver) that the
        // source session relied on. Mark the link list dirty so the save
        // path actually PUTs the carried-over picks for the new session.
        if (Array.isArray(data.document_ids) && data.document_ids.length > 0) {
          this.linkedDocumentIds = data.document_ids.slice();
          this.useSpecificDocuments = true;
          this.linkedDocsDirty = true;
        }
        // Open Additional Settings so the coach is reminded of (and can
        // confirm) the replicated choices alongside the rest of their prior
        // configuration.
        this.showAdditionalSettings = true;
      }
      this.welcomeText = data.welcome_text;
      this.checkInText = data.check_in_text;
      this.approvalText = data.approval_text;
      this.denialText = data.denial_text;
      this.start = moment(data.start_at);
      // Subtracting one day for display purposes, this gets added back in before posting to the server
      this.end = moment(data.end_at);

      const paymentOptions = _.get(data, 'payment_options', []);
      const today = moment().startOf('day');
      paymentOptions.forEach((po) => {
        let paymentOptionNeedsSave = false;
        // Sanitize historically corrupted installment plans at load time.
        // A PO may arrive with installments that have null dates (from before
        // this path was validated). Drop them and recompute total + price so
        // the review UI and any subsequent save reflect only valid installments.
        // Prices at this layer are already in integer cents.
        let installmentPlan = po.installment_plan;
        let { price } = po;
        if (installmentPlan && _.isArray(installmentPlan.installments)) {
          const validInstallments = installmentPlan.installments.filter(
            (i) => i.date !== null,
          );
          if (validInstallments.length !== installmentPlan.installments.length) {
            paymentOptionNeedsSave = true;
            const recomputedTotal = _.sumBy(validInstallments, 'price');
            installmentPlan = {
              ...installmentPlan,
              installments: validInstallments,
              total: recomputedTotal,
            };
            if (po.payment_strategy === 'installments') {
              price = recomputedTotal;
            }
          }
        }

        // When replicating, any installment date from the past is almost
        // certainly a leftover from the original session (e.g. replicating
        // last year's schedule). If we let those through, the billing engine
        // would immediately charge them. Null them out so the row stays
        // visible with its amount but the coach must pick a fresh date —
        // the existing "all installment dates must be set" validation in
        // payment_option_form.vue then blocks save until it's corrected.
        if (
          isReplicating
          && installmentPlan
          && _.isArray(installmentPlan.installments)
        ) {
          const clearedInstallments = installmentPlan.installments.map((i) => {
            if (i.date && moment(i.date).isBefore(today, 'day')) {
              this.replicateInstallmentDatesCleared = true;
              return { ...i, date: null };
            }
            return i;
          });
          installmentPlan = {
            ...installmentPlan,
            installments: clearedInstallments,
          };
        }

        const option = {
          draft: isReplicating || paymentOptionNeedsSave,
          name: po.name,
          price,
          class_pass_quantity: po.class_pass_quantity,
          class_pass_expires_in_days: po.class_pass_expires_in_days,
          payment_strategy: po.payment_strategy,
          installment_plan: installmentPlan,
          sibling_discount_enabled: po.sibling_discount_enabled,
          status: po.status,
          discount_per_extra_wrestler: po.discount_per_extra_wrestler,
          sibling_count_for_discount: po.sibling_count_for_discount,
          allowed_payment_methods: po.allowed_payment_methods,
          deposit_amount: po.deposit_amount,
        };
        if (isReplicating) {
          // If this is a replication situation these all need a temp id
          option.newOptionId = this.nextNewOptionId();
        } else {
          // If we are editing an existing one, make sure the id is set so we don't spin out duplicate options/
          option.id = po.id;
        }

        this.paymentOptions.push(option);
      });

      // problem here is that I can't distinguish between the original roster syncer and additional sessions at all.
      // in other words in a replication, I want to skip the original roster syncer that was created.
      // This is held together by duct tape, hope, and probability. We sync roster names and message groups when a paid session is updated, as long as the user hasn't messed with it.
      // So a best guess here is to remove the syncer with the same name that we are replicating.

      const syncers = _.get(data, 'roster_syncers', []);
      syncers.forEach((syncer) => {
        // In a replication case, we want to attempt to not sync with the standalone roster for that session and only sync with additional stuff they added.
        if (isReplicating && syncer.roster.name === this.name) {
          return;
        }
        // In a replicate case we always nil out the syncer id, but copy the roster id in
        this.additionalRosterSyncers.push({
          id: isReplicating ? null : syncer.id,
          _destroy: false,
          roster_id: syncer.roster_id,
          roster: syncer.roster,
        });
      });

      // Check that box by default in a replicate case if we have any roster syncers beyond the first
      if (this.additionalRosterSyncers.length > 0) {
        this.syncMoreRosters = true;
      }
    },
    getExisting() {
      const vm = this;
      vm.loadingExisting = true;
      const url = vm.$apiService.paidSessionUrl(vm.existingSessionId);
      axios
        .get(url)
        .then((response) => {
          const { data } = response;
          vm.sessionId = data.id;
          vm.setExistingOrReplicateData(data, false);

          // subscription options... these are not replicatable so it's not in that DRYed up method
          const priceOptions = _.get(data, 'product.billing_plans', []);
          priceOptions.forEach((price) => {
            const pr = {
              draft: false,
              id: price.id,
              name: price.name,
              amount: price.amount,
              interval: price.interval,
              interval_count: price.interval_count,
              intervals_to_bill: price.intervals_to_bill,
              anchor_day_of_month: price.anchor_day_of_month,
              billing_starts_at: price.billing_starts_at,
              class_pass_quantity: price.class_pass_quantity,
              coupon_strategy: price.coupon_strategy,
              sibling_coupon_id: price.sibling_coupon?.id,
              third_sibling_coupon_id: price.third_sibling_coupon?.id,
              active: price.active,
              associated_fee_id: price.associated_fee_id, // wiq fees
              associated_tax_id: price.associated_tax_id,
              associated_one_time_fee_id: price.associated_one_time_fee_id, // user set prices (enrollment fees)
              one_time_fee_amount: price.one_time_fee_amount,
              is_wiq_fee: price.is_wiq_fee,
              is_tax_item: price.is_tax_item,
              is_one_time_fee: price.is_one_time_fee,
            };
            vm.subscriptionPrices.push(pr);
          });

          // Load linked document IDs (not replicatable)
          if (data.document_ids && data.document_ids.length > 0) {
            vm.linkedDocumentIds = data.document_ids;
            vm.useSpecificDocuments = true;
          }

          vm.loadingExisting = false;
          vm.loadTrixContent(vm.welcomeTextTrixEditor, vm.welcomeText);
          vm.loadTrixContent(vm.checkInTrixEditor, vm.checkInText);
          vm.loadTrixContent(vm.approvalTrixEditor, vm.approvalText);
          vm.loadTrixContent(vm.denialTrixEditor, vm.denialText);
        })
        .catch((error) => {
          console.log(error);
          vm.errorHandler(error);
        });
    },
    successHandler(name) {
      const vm = this;
      vm.loading = false;
      vm.$notificationManager.emit('show-toast', `${name} saved`, true);
      if (vm.newlyCreatedSessionId) {
        window.location.assign(
          `/paid_sessions/${vm.newlyCreatedSessionId}/created`,
        );
      } else {
        window.location.assign('/paid_sessions');
      }
    },
    errorHandler() {
      const vm = this;
      vm.loading = false;
      vm.errorMessage = 'Error, please try again later or contact support.';
      vm.error = true;
    },
    validate() {
      const vm = this;

      let errorMessage = 'Please ensure ';
      let error = false;

      if (!vm.name) {
        errorMessage += 'name, ';
        error = true;
      }
      if (vm.start === null && vm.requireDates) {
        errorMessage += 'start, ';
        error = true;
      }
      if (vm.end === null && vm.requireDates) {
        errorMessage += 'end, ';
        error = true;
      }

      if (vm.teamRegistration && !vm.selectedDivisionStandardId) {
        errorMessage += 'division criteria, ';
        error = true;
      }

      if (vm.accountChoiceRequired && !vm.wiqAccountType) {
        vm.errorMessage = 'Please choose what kind of account registrants get (full WrestlingIQ accounts or guest checkout).';
        vm.error = true;
        return false;
      }

      if (error) {
        errorMessage = errorMessage.slice(0, -2);
        errorMessage += ' is not empty.';
        vm.errorMessage = errorMessage;
        vm.error = true;
        return false;
      }

      if (!vm.recurring) {
        if (_.size(vm.paymentOptions) === 0) {
          vm.errorMessage = 'Paid sessions must have at least one payment option.';
          vm.error = true;
          return false;
        }

        // Block save if any installment plan still has a null-dated
        // installment. This catches the path where a coach replicates a
        // session (where we null out past-dated installments) and then
        // clicks save without opening the affected payment option to set
        // fresh dates. Without this guard, the next charge run would either
        // choke on the null date or silently skip the installment.
        const poWithNullDate = _.find(vm.paymentOptions, (po) => (
          po.payment_strategy === 'installments'
          && _.some(_.get(po, 'installment_plan.installments', []), (i) => i.date === null)
        ));
        if (poWithNullDate) {
          vm.errorMessage = `Payment option "${poWithNullDate.name}" has an installment without a date. Click Edit on it and set a date for every installment before saving.`;
          vm.error = true;
          return false;
        }
      } else if (_.filter(vm.subscriptionPrices, ['active', true]).length === 0) {
        vm.errorMessage = 'Membership options must have at least one active subscription price.';
        vm.error = true;
        return false;
      }

      return true;
    },
    getPlainTrixText(trixEditor) {
      if (trixEditor) {
        return trixEditor.getDocument().toString();
      }
      return '';
    },
    checkIfFinished() {
      const vm = this;
      // some checks if any still have a draft status
      if (!_.some(vm.paymentOptions, ['draft', true])) {
        // All have finished
        vm.successHandler(vm.name);
      }
    },
    // used for creating and updating payment options
    getPaymentOptionJson(po, paid_session_id) {
      return {
        payment_option: {
          paid_session_id,
          name: po.name,
          price: po.price,
          class_pass_quantity: po.class_pass_quantity,
          class_pass_expires_in_days: po.class_pass_expires_in_days,
          payment_strategy: po.payment_strategy,
          installment_plan: po.installment_plan,
          anchor_day_of_month: po.anchor_day_of_month,
          sibling_discount_enabled: po.sibling_discount_enabled,
          discount_per_extra_wrestler: po.discount_per_extra_wrestler,
          sibling_count_for_discount: po.sibling_count_for_discount,
          allowed_payment_methods: po.allowed_payment_methods,
          status: po.status,
          deposit_amount: po.deposit_amount,
        },
      };
    },
    findOrCreatePaymentOption(po, paid_session_id) {
      const vm = this;
      if (!po.draft) {
        // Skip non draft payment options
        return;
      }

      const params = vm.getPaymentOptionJson(po, paid_session_id);
      if (po.id) {
        // PUT
        const url = vm.$apiService.paymentOptionUrl(po.id);
        axios
          .put(url, params)
          .then((response) => {
            po.draft = false;
            vm.checkIfFinished();
          })
          .catch((error) => {
            vm.errorHandler(error);
          });
      } else {
        // POST
        const url = vm.$apiService.paymentOptionsUrl();
        axios
          .post(url, params)
          .then((response) => {
            po.draft = false;
            vm.checkIfFinished();
          })
          .catch((error) => {
            vm.errorHandler(error);
          });
      }
    },
    onUseSpecificDocumentsChange() {
      const vm = this;
      if (vm.useSpecificDocuments) {
        vm.linkedDocsDirty = true;
        return;
      }
      if (vm.linkedDocumentIds.length > 0) {
        vm.linkedDocumentIds = [];
      }
      vm.linkedDocsDirty = true;
    },
    fetchAvailableDocuments() {
      const vm = this;
      const url = vm.$apiService.documentsUrl();
      vm.$apiService
        .loadAllPages(url, 'documents')
        .then((docs) => {
          vm.availableDocuments = docs.filter(
            (doc) => (doc.needs_signature || doc.liability_form) && !doc.coaches_only,
          );
        })
        .catch((error) => {
          console.error(error);
          vm.availableDocuments = [];
        });
    },
    saveDocumentLinks(paidSessionId) {
      const vm = this;
      if (!vm.linkedDocsDirty) {
        return Promise.resolve();
      }
      const url = vm.$apiService.paidSessionDocumentsUrl(paidSessionId);
      return axios
        .put(url, { document_ids: vm.linkedDocumentIds })
        .then(() => {
          vm.linkedDocsDirty = false;
        });
    },
    savePaymentOptions(paid_session_id) {
      const vm = this;
      if (_.some(vm.paymentOptions, ['draft', true])) {
        // If we have draft payment options, save them
        vm.paymentOptions.forEach((po) => {
          vm.findOrCreatePaymentOption(po, paid_session_id);
        });
      } else {
        // else just fire the success handler as we are all saved up
        vm.successHandler(vm.name);
      }
    },
    // used for creating and updating payment options
    getSubPriceJson(price) {
      return {
        billing_plan: {
          name: price.name,
          amount: price.amount,
          currency: price.currency,
          interval: price.interval,
          interval_count: price.interval_count,
          intervals_to_bill: price.intervals_to_bill,
          sibling_coupon_id: price.sibling_coupon_id,
          anchor_day_of_month: price.anchor_day_of_month,
          billing_starts_at: price.billing_starts_at,
          third_sibling_coupon_id: price.third_sibling_coupon_id,
          one_time_fee_amount: price.one_time_fee_amount,
          class_pass_quantity: price.class_pass_quantity,
          usage_type: 'licensed',
          active: price.active,
        },
      };
    },
    checkIfPricesFinished() {
      const vm = this;
      // some checks if any still have a draft status
      if (!_.some(vm.subscriptionPrices, ['draft', true])) {
        // All have finished
        vm.successHandler(vm.name);
      }
    },
    updateOrCreateSubscriptionPrice(price, paid_session_id) {
      const vm = this;
      if (!price.draft) {
        // Skip non draft payment options
        return;
      }

      const params = vm.getSubPriceJson(price);
      if (price.id) {
        // PUT
        const url = vm.$apiService.billingPlanUrl(price.id);
        axios
          .put(url, params)
          .then((response) => {
            price.draft = false;
            vm.checkIfPricesFinished();
          })
          .catch((error) => {
            vm.errorHandler(error);
          });
      } else {
        // POST
        const url = vm.$apiService.paidSessionBillingPlansUrl(paid_session_id);
        axios
          .post(url, params)
          .then((response) => {
            price.draft = false;
            vm.checkIfPricesFinished();
          })
          .catch((error) => {
            vm.errorHandler(error);
          });
      }
    },
    savePrices(paid_session_id) {
      const vm = this;
      if (_.some(vm.subscriptionPrices, ['draft', true])) {
        // If we have draft payment options, save them
        vm.subscriptionPrices.forEach((price) => {
          vm.updateOrCreateSubscriptionPrice(price, paid_session_id);
        });
      } else {
        // else just fire the success handler as we are all saved up
        vm.successHandler(vm.name);
      }
    },
    pingServer: _.throttle(function () {
      const vm = this;
      if (vm.loading === true) {
        return;
      }

      if (vm.validate()) {
        vm.loading = true;

        const welcome = $('#welcome-editor').val();
        const plainWelcome = vm.getPlainTrixText(vm.welcomeTextTrixEditor);
        const checkIn = $('#check-in-editor').val();
        const plainCheckIn = vm.getPlainTrixText(vm.checkInTrixEditor);
        const approval = $('#approval-editor').val();
        const plainApproval = vm.getPlainTrixText(vm.approvalTrixEditor);
        const denial = $('#denial-editor').val();
        const plainDenial = vm.getPlainTrixText(vm.denialTrixEditor);

        const params = {
          paid_session: {
            name: vm.name,
            start_at: vm.start ? vm.start.toISOString() : null,
            end_at: vm.end ? vm.end.toISOString() : null,
            registration_open: vm.registrationOpen,
            show_on_team_reg_page: vm.showOnTeamRegPage,
            // We only allow insurance on these specific types. This is a bit of a hack to avoid a larger refactor
            show_insurance:
              (vm.campOrClinic || vm.fullSeason)
              && vm.billingPartnerAllowsInsurance
                ? vm.showInsurance
                : false,
            exclude_from_g_sib_discount:
              vm.dropin || vm.trial || vm.teamRegistration
                ? true
                : vm.excludeFromGSibDiscount,
            allow_multiple_signups_per_wrestler:
              vm.allowMultipleSignupsPerWrestler,
            usaw_override: vm.usawOverride,
            aau_override: vm.aauOverride,
            division_standard_id: vm.teamRegistration
              ? vm.selectedDivisionStandardId
              : null,
            team_registration_divisions_attributes: vm.teamRegistration
              ? vm.buildTeamRegistrationDivisionsAttributes()
              : [],
            session_type: vm.sessionType,
            capacity_limit: vm.capacityLimitParam,
            location_id: vm.selectedLocationId,
            participants: vm.participants,
            team_id: vm.teamId,
            welcome_text: welcome,
            plain_welcome_text: plainWelcome,
            check_in_text: checkIn,
            plain_check_in_text: plainCheckIn,
            approval_text: vm.teamRegistration ? approval : null,
            plain_approval_text: vm.teamRegistration ? plainApproval : null,
            denial_text: vm.teamRegistration ? denial : null,
            plain_denial_text: vm.teamRegistration ? plainDenial : null,
            upsell_product_id:
              vm.allowUpsell && vm.upsellProduct ? vm.upsellProduct.id : null,
            upsell_required:
              vm.allowUpsell && vm.upsellProduct ? vm.upsellRequired : false,
            roster_syncers_attributes: [],
          },
        };

        // Only send the account type when the coach actually chose one (camp/clinic
        // and drop in). Other types are inferred server-side, and omitting the key on
        // updates leaves the (immutable) column untouched.
        if (vm.accountChoiceRequired && vm.wiqAccountType) {
          params.paid_session.wiq_account_type = vm.wiqAccountType;
        }

        if (vm.notAnonymous) {
          vm.additionalRosterSyncers.forEach((rosterSyncer) => {
            params.paid_session.roster_syncers_attributes.push({
              id: rosterSyncer.id,
              _destroy: rosterSyncer._destroy,
              roster_id: rosterSyncer.roster_id,
            });
          });
        }

        vm.loading = true;

        if (vm.sessionId) {
          // PUT
          const url = vm.$apiService.paidSessionUrl(vm.existingSessionId);
          axios
            .put(url, params)
            .then((response) => {
              vm.saveDocumentLinks(vm.existingSessionId)
                .then(() => {
                  if (vm.recurring) {
                    vm.savePrices(vm.existingSessionId);
                  } else {
                    vm.savePaymentOptions(vm.existingSessionId);
                  }
                })
                .catch((error) => {
                  vm.errorHandler(error);
                });
            })
            .catch((error) => {
              vm.errorHandler(error);
            });
        } else {
          // POST
          // Make sure we set nested roster attributes first if a roster is requested
          if (vm.createRoster && vm.notAnonymous) {
            params.paid_session.roster_syncers_attributes.push({
              roster_attributes: {
                name: vm.name,
                team_id: vm.teamId,
              },
            });
          }
          if (vm.createRoster && vm.notAnonymous) {
            params.paid_session.all_coaches_in_message_group = vm.allCoachesInMessageGroup;
            if (!vm.allCoachesInMessageGroup) {
              // The current user is always part of the group (pinned chip),
              // so make sure their id is in the payload alongside any coaches
              // they explicitly added via search.
              const ids = vm.selectedCoaches.map((c) => c.id);
              if (vm.currentProfileId && !ids.includes(vm.currentProfileId)) {
                ids.unshift(vm.currentProfileId);
              }
              params.paid_session.message_group_coach_profile_ids = ids;
            }
          }

          const url = vm.$apiService.paidSessionsUrl();
          axios
            .post(url, params)
            .then((response) => {
              vm.newlyCreatedSessionId = response.data.id;
              vm.saveDocumentLinks(response.data.id)
                .then(() => {
                  if (vm.recurring) {
                    vm.savePrices(response.data.id);
                  } else {
                    vm.savePaymentOptions(response.data.id);
                  }
                })
                .catch((error) => {
                  vm.errorHandler(error);
                });
            })
            .catch((error) => {
              vm.errorHandler(error);
            });
        }
      }
    }, 500),
    dateRangeChanged(start, end) {
      this.start = start;
      this.end = end;
    },
  },
};
</script>
